import React, { useEffect, useState } from 'react';
import { postAPI } from '../../utils/utils';
import './SponsorSuccess.css';

/**
 * SponsorSuccess - Success page shown after sponsor completes payment
 */
function SponsorSuccess() {
    const [status, setStatus] = useState('processing'); // processing, success, already_subscribed, reactivated, error
    const [errorMessage, setErrorMessage] = useState('');
    const [tierName, setTierName] = useState('');
    const [refunded, setRefunded] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        // Server-side redirect flow: backend already processed the payment
        const redirectStatus = urlParams.get('status');
        if (redirectStatus) {
            const tier = urlParams.get('tier') || '';
            setTierName(tier);

            if (redirectStatus === 'duplicate') {
                setStatus('already_subscribed');
                setRefunded(urlParams.get('refunded') === 'true');
            } else if (redirectStatus === 'reactivated') {
                setStatus('reactivated');
            } else if (redirectStatus === 'success') {
                setStatus('success');
            } else {
                setStatus('error');
                setErrorMessage('Estat de pagament desconegut');
            }
            return;
        }

        // Client-side flow: confirm payment via API
        const sessionId = urlParams.get('session_id');

        if (!sessionId) {
            setStatus('error');
            setErrorMessage('No s\'ha trobat la sessió de pagament');
            return;
        }

        postAPI('/sponsors/confirm-payment', { sessionId }, (data) => {
            if (data?.success) {
                if (data.alreadySubscribed) {
                    setStatus('already_subscribed');
                    setRefunded(data.refunded || false);
                } else if (data.reactivated) {
                    setStatus('reactivated');
                } else {
                    setStatus('success');
                }
                setTierName(data.tierName || '');
            } else {
                setStatus('error');
                setErrorMessage(data?.message || 'Error processant el pagament');
            }
        }, (err) => {
            setStatus('error');
            setErrorMessage('Error de connexió');
        });
    }, []);

    if (status === 'processing') {
        return (
            <div className="sponsor-success-page">
                <div className="sponsor-success-card">
                    <div className="icon-container sponsor-processing-icon">
                        <svg className="spinner" viewBox="0 0 50 50">
                            <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="4"></circle>
                        </svg>
                    </div>
                    <h1>Confirmant pagament...</h1>
                    <p className="success-message">
                        Estem verificant les dades del teu patrocini.
                    </p>
                </div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="sponsor-success-page">
                <div className="sponsor-success-card sponsor-card-error">
                    <div className="icon-container sponsor-error-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                    </div>
                    <h1>Alguna cosa no ha anat bé</h1>
                    <p className="success-message">
                        {errorMessage}
                    </p>
                    <p className="success-details">
                        Si el càrrec s'ha realitzat al teu banc, contacta amb la colla per resoldre-ho manualment.
                    </p>
                </div>
            </div>
        );
    }

    if (status === 'already_subscribed') {
        return (
            <div className="sponsor-success-page">
                <div className="sponsor-success-card sponsor-card-info">
                    <div className="icon-container sponsor-info-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                    </div>
                    <h1>Ja ets patrocinador!</h1>
                    {tierName ? (
                        <div className="tier-badge">
                            <span>Patrocinador {tierName}</span>
                        </div>
                    ) : null}
                    <p className="success-message">
                        Ja tens una subscripció activa per aquest nivell de patrocini.
                    </p>
                    {refunded ? (
                        <p className="success-details refund-notice">
                            <strong>✓ El pagament duplicat ha estat reemborsat automàticament.</strong>
                        </p>
                    ) : null}
                    <p className="success-details">
                        Si vols canviar de nivell o modificar la teva subscripció, contacta amb la colla.
                    </p>
                    <div className="success-footer">
                        <p>Gràcies pel teu suport!</p>
                    </div>
                </div>
            </div>
        );
    }

    if (status === 'reactivated') {
        return (
            <div className="sponsor-success-page">
                <div className="sponsor-success-card">
                    <div className="icon-container sponsor-success-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                    </div>
                    <h1>Benvingut/da de nou!</h1>
                    {tierName ? (
                        <div className="tier-badge">
                            <span>Patrocinador {tierName}</span>
                        </div>
                    ) : null}
                    <p className="success-message">
                        La teva subscripció s'ha reactivat correctament.
                    </p>
                    <p className="success-details">
                        Rebràs un correu electrònic amb els detalls de la teva subscripció.
                    </p>
                    <div className="success-footer">
                        <p>Gràcies per tornar a fer pinya amb nosaltres!</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="sponsor-success-page">
            <div className="sponsor-success-card">
                <div className="icon-container sponsor-success-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                </div>
                <h1>Moltes gràcies!</h1>
                {tierName ? (
                    <div className="tier-badge">
                        <span>Patrocinador {tierName}</span>
                    </div>
                ) : null}
                <p className="success-message">
                    El teu patrocini s'ha activat correctament.
                </p>
                <p className="success-details">
                    Rebràs un correu electrònic amb els detalls de la teva subscripció.
                </p>
                <div className="success-footer">
                    <p>Gràcies per fer pinya amb nosaltres!</p>
                </div>
            </div>
        </div>
    );
}

export default SponsorSuccess;
